/*
 * Copyright (C) 2025 - present by Potentially
 *
 * Please see distribution for license.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnChanges, SimpleChanges, inject, input, output, signal } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DEFAULT_PDFIUM_WASM_URL, createPdfiumWorkerEngine } from '@embedpdf/engines';
import { PdfDocumentObject } from '@embedpdf/models';

@Component({
  selector: 'ptl-pdf-pages-renderer',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: './pdf-pages-renderer.component.html',
  styleUrl: './pdf-pages-renderer.component.scss',
})
export class PdfPagesRendererComponent implements OnChanges {
  pdfUrl = input.required<string>();

  pageImageUrls = signal<string[]>([]);
  loading = signal(true);
  error = signal<string | undefined>(undefined);
  loaded = output<void>();

  private engine: ReturnType<typeof createPdfiumWorkerEngine> | undefined;
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.destroyRef.onDestroy(() => this.cleanup());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfUrl']) {
      this.renderPdfPages();
    }
  }

  private async renderPdfPages(): Promise<void> {
    this.revokeUrls();
    this.loading.set(true);
    this.error.set(undefined);

    const url = this.pdfUrl();
    if (!url) {
      this.loading.set(false);
      return;
    }

    try {
      if (!this.engine) {
        this.engine = createPdfiumWorkerEngine(DEFAULT_PDFIUM_WASM_URL);
      }

      // Open the PDF document
      const pdfDocument: PdfDocumentObject = await this.engine.openDocumentUrl({ id: url, url }).toPromise();

      // Render each page to a blob
      const imageUrls: string[] = [];
      for (let i = 0; i < pdfDocument.pageCount; i++) {
        const blob: Blob = await this.engine
          .renderPage(pdfDocument, pdfDocument.pages[i], {
            scaleFactor: 2,
            imageType: 'image/png',
          })
          .toPromise();
        imageUrls.push(URL.createObjectURL(blob));
      }

      this.pageImageUrls.set(imageUrls);
      this.loading.set(false);
      this.loaded.emit();
    } catch (e) {
      console.error('Failed to render PDF pages', e);
      this.error.set('Failed to render PDF');
      this.loading.set(false);
      this.loaded.emit();
    }
  }

  private revokeUrls(): void {
    for (const url of this.pageImageUrls()) {
      URL.revokeObjectURL(url);
    }
    this.pageImageUrls.set([]);
  }

  private cleanup(): void {
    this.revokeUrls();
    if (this.engine) {
      this.engine.destroy();
      this.engine = undefined;
    }
  }
}
